import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Row, Statistic, Tag } from 'antd';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	PhoneOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	CalendarOutlined,
	MobileOutlined,
} from '@ant-design/icons';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as ReTooltip,
	Legend,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../context/auth.context';
import AppLayout from '../../layouts/app.layout';
import axios from '../../config/axios';
import { formatNumber, handleAPIError } from '../../utils/helpers';
import dayjs from '../../config/dayjs';

interface PeriodStat { count: number; amount: number }

interface DashboardData {
	collection_reachout_today: { called: number; not_called: number; total_overdue_devices: number };
	collection_reachout_yesterday: { called: number; not_called: number; total_overdue_devices: number };
	due_today: PeriodStat;
	due_yesterday: PeriodStat;
	collected_today: PeriodStat;
	collected_yesterday: PeriodStat;
	due_this_week: PeriodStat;
	due_last_week: PeriodStat;
	due_mtd: PeriodStat;
	due_this_month?: PeriodStat;
	settled_mtd?: PeriodStat;
	settled_last_month_same_date?: PeriodStat;
	due_last_month_same_date: PeriodStat;
	paid_this_week: PeriodStat;
	paid_last_week: PeriodStat;
	paid_mtd: PeriodStat;
	paid_last_month_same_date: PeriodStat;
	verified_devices: { mtd: number; last_month_same_date: number };
	active_devices: number;
	overdue_devices: number;
	written_off_mtd: { count: number; amount: number };
	written_off_to_date: { count: number; amount: number };
	daily_trend: { date: string; due_count: number; due_amount: number; paid_count: number; paid_amount: number }[];
}

const pct = (a: number, b: number) => (b === 0 ? null : Math.round(((a - b) / b) * 100));

const TrendTag = ({ current, previous, label = 'vs last month' }: { current: number; previous: number; label?: string }) => {
	const diff = pct(current, previous);
	if (diff === null) return null;
	const up = diff >= 0;
	return (
		<Tag color={up ? 'green' : 'red'} className="ml-1 mt-1">
			{up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(diff)}% {label}
		</Tag>
	);
};

const fmt = (n: number) => `UGX ${formatNumber(n)}`;

const PIE_COLORS = ['#52c41a', '#ff4d4f'];

const AdminDashboard = () => {
	const { user } = useContext(AuthContext);

	const { data, error, isLoading } = useQuery<DashboardData>({
		queryKey: ['admin-dashboard'],
		queryFn: async () => {
			const res = await axios.get('/admin/reports/dashboard', { withCredentials: true });
			return res.data.results;
		},
		refetchInterval: 5 * 60 * 1000,
	});

	if (error) handleAPIError(error);

	if (user?.admin_type === 'insurance') {
		return (
			<AppLayout title="Dashboard" subtitle="Manage your activities and view statistics here.">
				<div className="flex flex-col items-center justify-center gap-6 py-12">
					<div className="text-center">
						<h2 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white">Welcome to Insurance Dashboard</h2>
						<p className="mb-6 text-gray-600 dark:text-gray-300">Access and manage your reports</p>
					</div>
					<Link to="/insurance/reports">
						<Button type="primary" size="large" className="h-auto px-8 py-4 font-medium">
							View Reports
						</Button>
					</Link>
				</div>
			</AppLayout>
		);
	}

	if (user && user.admin_type !== 'super_admin') {
		return (
			<AppLayout title="Dashboard" subtitle="Manage your activities and view statistics here.">
				<div className="flex flex-col items-center justify-center py-24">
					<p className="text-gray-500">You do not have permission to view this dashboard.</p>
				</div>
			</AppLayout>
		);
	}

	const d = data;
	const today = dayjs().format('DD MMM YYYY');

	const pieData = d
		? [
				{ name: 'Called', value: d.collection_reachout_today.called },
				{ name: 'Not Called', value: d.collection_reachout_today.not_called },
		  ]
		: [];

	const trendData = (d?.daily_trend || []).map((row) => ({
		...row,
		label: dayjs(row.date).format('DD MMM'),
	}));

	return (
		<AppLayout title="Dashboard" subtitle={`Live overview · ${today}`}>
			<div className="space-y-6">

				{/* ── Top KPIs ── */}
				<Row gutter={[16, 16]}>
					<Col xs={24} sm={12} lg={6}>
						<Card className="h-full shadow-sm" loading={isLoading}>
							<Statistic
								title="Active Contracts"
								value={d?.active_devices ?? '—'}
								prefix={<MobileOutlined />}
								valueStyle={{ color: '#1890ff' }}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<Card className="h-full shadow-sm" loading={isLoading}>
							<Statistic
								title="Overdue Contracts"
								value={d?.overdue_devices ?? '—'}
								prefix={<ExclamationCircleOutlined />}
								valueStyle={{ color: '#ff4d4f' }}
							/>
							{d && (
								<div className="mt-1 text-xs text-gray-500">
									{Math.round((d.overdue_devices / Math.max(d.active_devices, 1)) * 100)}% of active
								</div>
							)}
						</Card>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<Card className="h-full shadow-sm" loading={isLoading}>
							<Statistic
								title="Written Off"
								value={d ? formatNumber(Math.round(d.written_off_to_date.amount)) : '—'}
								prefix="UGX"
								valueStyle={{ color: '#8c8c8c' }}
							/>
							{d && (
								<div className="mt-1 text-xs text-gray-500">
									{d.written_off_to_date.count} contract
									{d.written_off_to_date.count === 1 ? '' : 's'}
									{d.written_off_mtd.count > 0 &&
										` · ${d.written_off_mtd.count} this month`}
								</div>
							)}
						</Card>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<Card className="h-full shadow-sm" loading={isLoading}>
							<Statistic
								title="Verified Devices (MTD)"
								value={d?.verified_devices.mtd ?? '—'}
								prefix={<CalendarOutlined />}
								valueStyle={{ color: '#722ed1' }}
							/>
							{d && <TrendTag current={d.verified_devices.mtd} previous={d.verified_devices.last_month_same_date} />}
						</Card>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<Card className="h-full shadow-sm" loading={isLoading}>
							<Statistic
								title="Collection Efficiency (MTD)"
								value={d?.settled_mtd ? Math.round((d.settled_mtd.count / Math.max(d.due_mtd.count, 1)) * 100) : '—'}
								suffix="%"
								prefix={<CheckCircleOutlined />}
								valueStyle={{ color: '#52c41a' }}
							/>
							{d?.settled_mtd && (
								<div className="mt-1 text-xs text-gray-500">
									{d.settled_mtd.count} of {d.due_mtd.count} due installments settled
								</div>
							)}
							{d?.settled_mtd && d?.settled_last_month_same_date && (
								<TrendTag
									current={Math.round((d.settled_mtd.count / Math.max(d.due_mtd.count, 1)) * 100)}
									previous={Math.round((d.settled_last_month_same_date.count / Math.max(d.due_last_month_same_date.count, 1)) * 100)}
								/>
							)}
						</Card>
					</Col>
				</Row>

				{/* ── Today ── */}
				<div>
					<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Today</h3>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12} lg={8}>
							<Card title="Due Today" className="shadow-sm" loading={isLoading}>
								<Statistic value={d?.due_today.count ?? '—'} suffix="installments" />
								<div className="mt-1 text-lg font-semibold text-orange-500">
									{d ? fmt(d.due_today.amount) : '—'}
								</div>
								{d && <TrendTag current={d.due_today.count} previous={d.due_yesterday.count} label="vs yesterday" />}
							</Card>
						</Col>
						<Col xs={24} sm={12} lg={8}>
							<Card title="Collected Today" className="shadow-sm" loading={isLoading}>
								<Statistic
									value={d?.collected_today.count ?? '—'}
									suffix="payments"
									valueStyle={{ color: '#52c41a' }}
								/>
								<div className="mt-1 text-lg font-semibold text-green-600">
									{d ? fmt(d.collected_today.amount) : '—'}
								</div>
								{d && <TrendTag current={d.collected_today.count} previous={d.collected_yesterday.count} label="vs yesterday" />}
							</Card>
						</Col>
						<Col xs={24} lg={8}>
							<Card
								title={<span><PhoneOutlined className="mr-2" />Collection Reach-Out</span>}
								className="shadow-sm"
								loading={isLoading}
							>
								{d && (
									<div className="flex items-center gap-4">
										<PieChart width={110} height={110}>
											<Pie data={pieData} cx={50} cy={50} innerRadius={28} outerRadius={50} dataKey="value">
												{pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
											</Pie>
										</PieChart>
										<div className="space-y-2">
											<div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Today</div>
											<div><Tag color="success">{d.collection_reachout_today.called} Called</Tag></div>
											<div><Tag color="error">{d.collection_reachout_today.not_called} Not Called</Tag></div>
											<div className="mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Yesterday</div>
											<div><Tag color="success">{d.collection_reachout_yesterday.called} Called</Tag></div>
											<div><Tag color="error">{d.collection_reachout_yesterday.not_called} Not Called</Tag></div>
											<div className="mt-1 text-xs text-gray-400">
												{d.collection_reachout_today.total_overdue_devices} overdue total
											</div>
										</div>
									</div>
								)}
							</Card>
						</Col>
					</Row>
				</div>

				{/* ── This Week & MTD ── */}
				<div>
					<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">This Week &amp; Month to Date</h3>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12} lg={6}>
							<Card title="Due This Week" className="shadow-sm" loading={isLoading}>
								<Statistic value={d?.due_this_week.count ?? '—'} suffix="installments" />
								<div className="mt-1 font-semibold text-orange-500">
									{d ? fmt(d.due_this_week.amount) : '—'}
								</div>
								{d && <TrendTag current={d.due_this_week.count} previous={d.due_last_week.count} label="vs last week" />}
							</Card>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Card title="Paid This Week" className="shadow-sm" loading={isLoading}>
								<Statistic value={d?.paid_this_week.count ?? '—'} suffix="payments" valueStyle={{ color: '#52c41a' }} />
								<div className="mt-1 font-semibold text-green-600">
									{d ? fmt(d.paid_this_week.amount) : '—'}
								</div>
								{d && <TrendTag current={d.paid_this_week.count} previous={d.paid_last_week.count} label="vs last week" />}
							</Card>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Card title="Due MTD" className="shadow-sm" loading={isLoading}>
								<Statistic value={d?.due_mtd.count ?? '—'} suffix="installments" />
								<div className="mt-1 font-semibold text-orange-500">
									{d ? fmt(d.due_mtd.amount) : '—'}
								</div>
								{d && <TrendTag current={d.due_mtd.count} previous={d.due_last_month_same_date.count} />}
							</Card>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Card title="Paid MTD" className="shadow-sm" loading={isLoading}>
								<Statistic value={d?.paid_mtd.count ?? '—'} suffix="payments" valueStyle={{ color: '#52c41a' }} />
								<div className="mt-1 font-semibold text-green-600">
									{d ? fmt(d.paid_mtd.amount) : '—'}
								</div>
								{d && <TrendTag current={d.paid_mtd.count} previous={d.paid_last_month_same_date.count} />}
							</Card>
						</Col>
					</Row>
				</div>

				{/* ── MTD vs Last Month ── */}
				{d && (
					<div>
						<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
							MTD vs Last Month (same date)
						</h3>
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12}>
								<Card title="Payments Collected" className="shadow-sm">
									<ResponsiveContainer width="100%" height={160}>
										<BarChart
											data={[
												{ name: 'Last Month', count: d.paid_last_month_same_date.count },
												{ name: 'This Month', count: d.paid_mtd.count },
											]}
											margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
										>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis dataKey="name" tick={{ fontSize: 12 }} />
											<YAxis tick={{ fontSize: 11 }} />
											<ReTooltip formatter={(v: number) => v.toLocaleString()} />
											<Bar dataKey="count" name="Payments" fill="#52c41a" radius={[4, 4, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								</Card>
							</Col>
							<Col xs={24} md={12}>
								<Card title="Verified Devices" className="shadow-sm">
									<ResponsiveContainer width="100%" height={160}>
										<BarChart
											data={[
												{ name: 'Last Month', count: d.verified_devices.last_month_same_date },
												{ name: 'This Month', count: d.verified_devices.mtd },
											]}
											margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
										>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis dataKey="name" tick={{ fontSize: 12 }} />
											<YAxis tick={{ fontSize: 11 }} />
											<ReTooltip />
											<Bar dataKey="count" name="Verified Devices" fill="#722ed1" radius={[4, 4, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								</Card>
							</Col>
						</Row>
					</div>
				)}

				{/* ── 14-day trend ── */}
				{trendData.length > 0 && (
					<Card title="14-Day Payment Trend (Due vs Paid)" className="shadow-sm">
						<ResponsiveContainer width="100%" height={260}>
							<BarChart data={trendData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="label" tick={{ fontSize: 11 }} />
								<YAxis tick={{ fontSize: 11 }} />
								<ReTooltip formatter={(v: number) => v.toLocaleString()} />
								<Legend />
								<Bar dataKey="due_count" name="Due" fill="#faad14" radius={[4, 4, 0, 0]} />
								<Bar dataKey="paid_count" name="Paid" fill="#52c41a" radius={[4, 4, 0, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</Card>
				)}

			</div>
		</AppLayout>
	);
};

export default AdminDashboard;
